<template>
  <div
    class="hamburger-dim"
    :class="isHamburger ? 'on' : ''"
    @click.self="closeMenu"
    :style="isHamburger ? 'z-index:999' : 'z-index:-1'"
  >
    <div class="hamburger-panel" @click.stop>
      <div class="hamburger-header">
        <h2 class="hamburger-title">메뉴</h2>
        <button class="close-btn" @click="closeMenu" aria-label="닫기">
          <span class="close-x">×</span>
        </button>
      </div>

      <div class="hamburger-list">
        <template v-for="(section, si) in sections" :key="si">
          <div class="section-header">
            <img v-if="section.icon" :src="section.icon" class="section-icon" alt="" />
            <span>{{ section.label }}</span>
          </div>
          <ul class="section-list">
            <li
              v-for="item in section.items"
              :key="item.label"
              class="hamburger-item"
              :class="{ 'is-pending': item.pending }"
              @click="fnClick(item)"
            >
              <span class="item-label">
                {{ item.label }}
                <em v-if="item.pending" class="pending-tag">준비중</em>
                <em v-else-if="item.external" class="external-tag">새 창</em>
              </span>
              <i class="ico-arrow-right"></i>
            </li>
          </ul>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import store from '@/store';
import router from '@/router';
import ServiceExec from '@/common/utils/ServiceExec';
import { config } from '@/common/service/apiList';
import { showTextModal, showDoneModal, show, modalList } from '@/common/utils/ModalState';
import { CODE_ENV_LIST } from '@/common/constants/ConstCode';
// 260511 오후회의 #2: 섹션 제목 옆 미니멀 라인 아이콘
import secFrequent from '@/assets/static/images/hamburger/sec-frequent.svg';
import secRecord from '@/assets/static/images/hamburger/sec-record.svg';
import secBenefit from '@/assets/static/images/hamburger/sec-benefit.svg';
import secExternal from '@/assets/static/images/hamburger/sec-external.svg';
import secSupport from '@/assets/static/images/hamburger/sec-support.svg';
import secMe from '@/assets/static/images/hamburger/sec-me.svg';

const isHamburger = computed(() => store.getters['layout/getIsHamburger']);

// 260629 커피쿠폰 공유이벤트(coffee_event.enabled) 진행 중일 때만 쿠폰함을 라이브(/event/coupon)로 노출.
//   더보기(more/index.vue)와 동일 게이트 — 플래그 OFF면 '준비중'으로 폴백(기존 동작 유지).
const coffeeEventEnabled = computed(() => !!store.getters['common/getFeatureFlags']?.coffeeEventEnabled);

// 260902 교회 채널 온라인 추모관 오픈(memorial.church_channel.enabled) — 위 쿠폰함과 동일 게이트.
//   화면·라우트는 이미 배포돼 있고 진입 링크만 없던 상태라, 링크 복구와 플래그를 한 배포에 같이 넣는다.
//   OFF면 '준비중'으로 폴백(라우터 가드가 URL 직행도 함께 막는다 — v-if만으로는 딥링크가 뚫린다).
const memorialChurchEnabled = computed(() => !!store.getters['common/getFeatureFlags']?.memorialChurchEnabled);

// 260624 마이노트(쉼) = 웰다잉 '하나님께 드리는 편지'(/welldying/my/WELL_GOD) 화이트리스트 오픈.
// 허용 유저(대표 형 + 와쳐 4명)만 메뉴를 path로 노출, 나머지는 그대로 '준비중'.
// 햄버거를 열 때마다 fresh 조회(운영 DB row 토글 즉시 반영) + fail-closed(미로그인/오류=숨김).
const mynoteEnabled = ref(false);
const fetchMynoteAccess = async () => {
  const { userNo } = store.getters['user/getUserData'] || {};
  if (!userNo) {
    mynoteEnabled.value = false;
    return;
  }
  try {
    const { data, status } = await ServiceExec.get(config.mynoteAccess);
    mynoteEnabled.value = status === ServiceExec.status.OK && !!(data && data.enabled);
  } catch (_) {
    mynoteEnabled.value = false;
  }
};
watch(isHamburger, opened => {
  if (opened) fetchMynoteAccess();
});

const FLOWER_URL =
  'https://shop5.flowerbiz.co.kr/gb/home?agencyid=jumpjump&shopid=frBusiness&agencyidx=2766';

// 260422 Horcrux 자문 + 대표 성향 #1 "과하게 편리" 반영: 섹션별 대폭 확장
// computed: mynoteEnabled(화이트리스트) 변화 시 마이노트 항목 path↔준비중 토글 재렌더.
const sections = computed(() => [
  {
    label: '자주 쓰는',
    icon: secFrequent,
    items: [
      { label: '홈으로', path: '/main' },
      { label: '내 비즈니스', path: '/business/my' },
      { label: '내 간판 통계', path: '/more/stats' },
      { label: '내 명함', path: '/card/my' },
      // 260626 비즈톡: 받은/보낸 비즈톡 목록 진입. (미읽음 배지는 Slice 4 푸시/알림 인프라 때)
      { label: '비즈톡', path: '/talk' },
      // 260803 대표: 메인 사업자 화면이 길어져 관리 항목이 밀리는 문제 → 두 진입을 메인 카드에서 빼고 여기로.
      //   Main.vue 사업자 미들의 .collab-card / .fr-banner 제거분. 소비자 미들 창업관 배너는 그대로 둔다.
      { label: '함께하는 게시판', path: '/business/wanted' },
      { label: '모바일 창업관', path: '/business/franchise' },
      { label: '공유하기', action: 'kakaoShare' },
      { label: '관심 업종 고르기', path: '/more/interest-category' },
      { label: '친구 초대', pending: true }
    ]
  },
  {
    label: '내 기록',
    icon: secRecord,
    items: [
      // 260511 오후회의 #4: 추모관 앱 연결 예정으로 '준비중'이었으나,
      // 260626 레거시 마이노트 원래 플로우(비번 입력 → 웰다잉 선택)로 화이트리스트 한정 오픈.
      mynoteEnabled.value
        ? { label: '마이노트(쉼)', action: 'mynote' }
        : { label: '마이노트(쉼)', pending: true },
      // 260904 대표 컨펌 — 온라인 추모관은 교회 목록에서 교회를 고르고 들어간다(QR 랜딩 타일과 같은 목적지).
      memorialChurchEnabled.value
        ? { label: '온라인 추모관', path: '/cry/memorial/churches' }
        : { label: '온라인 추모관', pending: true },
      { label: '다이어리', path: '/diary' },
      { label: '좋아요 모아보기', path: '/more/like/business' }
    ]
  },
  {
    label: '혜택 · 쇼핑',
    icon: secBenefit,
    items: [
      coffeeEventEnabled.value
        ? { label: '쿠폰함', path: '/event/coupon' }
        : { label: '쿠폰함', pending: true },
      { label: '출석체크', pending: true },
      { label: '이벤트', pending: true },
      { label: '상품구매', path: '/more/item/buy' },
      { label: '내 상품', path: '/more/item' },
      { label: '결제내역', path: '/more/payment' },
      { label: '프리미엄 포인트', path: '/more/premium' }
    ]
  },
  {
    label: '외부 서비스',
    icon: secExternal,
    items: [
      { label: '경조사 화환', external: FLOWER_URL }
    ]
  },
  {
    label: '고객 지원',
    icon: secSupport,
    items: [
      { label: '공지사항', path: '/more/notice' },
      { label: '자주묻는질문', path: '/more/faq' },
      { label: '고객센터', path: '/more/inquiry' },
      { label: '알림', path: '/more/notification' },
      // 260521: 이용약관 — 마이페이지·햄버거 두 곳 동시 등록 (대표 컨펌)
      { label: '이용약관', path: '/signup/terms?title=서비스%20이용약관' }
    ]
  },
  {
    label: '내 정보',
    icon: secMe,
    items: [
      { label: '회원정보 수정', path: '/more/user/update' },
      { label: '비밀번호 변경', path: '/more/user/update/password' },
      { label: '설정', path: '/more/setting' }
    ]
  }
]);

const closeMenu = () => {
  store.commit('layout/setIsHamburger', false);
};

const fnClick = item => {
  if (item.pending) {
    showTextModal('준비중입니다.', '확인');
    return;
  }
  // 260626 마이노트(쉼): 원래 진입 플로우 복원 — 비번 미설정 시 설정 화면, 설정 시 비번 입력 모달(통과 후 /welldying/menu).
  if (item.action === 'mynote') {
    closeMenu();
    const { hasWellPassword } = store.getters['user/getUserData'] || {};
    // 260922 대표: 미설정도 페이지 이동 대신 같은 모달(setup) — '비번 새로 설정하게끔 팝업창'. 모달 안에서 숫자/패턴 선택 + 문자 인증.
    show(modalList.WELL_PASSWORD_MODAL, hasWellPassword ? { title: '웰다잉 비밀번호 입력' } : { title: '웰다잉 비밀번호 설정', mode: 'setup' });
    return;
  }
  if (item.action === 'signout') {
    showDoneModal('정말 로그아웃 하시겠습니까?', '확인', '취소', async () => {
      await store.dispatch('user/signOut');
      closeMenu();
      router.push('/more/user/delete');
    });
    return;
  }
  // 260428: 카카오 공유 — 받는 사람 입장 카피, 가입 시 24시간권 보상
  if (item.action === 'kakaoShare') {
    if (!window.Kakao || !window.Kakao.Share) {
      showTextModal('카카오톡을 열 수 없어요. 앱 업데이트 후 다시 시도해 주세요.', '확인');
      return;
    }
    const shareUrl = `${CODE_ENV_LIST.DYNAMIC_DOMAIN}/gate`;
    window.Kakao.Share.sendDefault({
      objectType: 'feed',
      content: {
        title: '🎁 24시간 무료 간판이 도착했어요',
        description: '지금 가입하면 내 가게가 24시간 메인에 무료로 노출돼요. 1분이면 시작!',
        imageUrl: `${CODE_ENV_LIST.DYNAMIC_DOMAIN}/og-jumpon-wide.png`,
        link: { mobileWebUrl: shareUrl, webUrl: shareUrl }
      },
      buttons: [
        { title: '간판ON 시작하기', link: { mobileWebUrl: shareUrl, webUrl: shareUrl } }
      ]
    });
    closeMenu();
    return;
  }
  if (item.external) {
    closeMenu();
    window.open(item.external, '_blank');
    return;
  }
  if (!item.path) {
    showTextModal('준비중입니다.', '확인');
    return;
  }
  closeMenu();
  router.push(item.path);
};
</script>

<style scoped>
.hamburger-dim {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0);
  pointer-events: none;
  transition: background 0.25s ease;
}
.hamburger-dim.on {
  background: rgba(0, 0, 0, 0.45);
  pointer-events: auto;
}

.hamburger-panel {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 68%;
  max-width: 280px;
  background: #fff;
  box-shadow: 2px 0 16px rgba(0, 0, 0, 0.2);
  transform: translateX(-100%);
  transition: transform 0.28s ease;
  display: flex;
  flex-direction: column;
}
.hamburger-dim.on .hamburger-panel {
  transform: translateX(0);
}

.hamburger-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.4rem 1.4rem 1rem;
  border-bottom: 1px solid #eee;
}
.hamburger-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
}
.close-btn {
  width: 28px;
  height: 28px;
  border: 0;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.close-x {
  font-size: 2.1rem;
  line-height: 1;
  color: #555;
}

.hamburger-list {
  list-style: none;
  margin: 0;
  /* 260428: 하단 네비바(약 64px) + iOS safe-area + 여유 — 마지막 항목 잘림 방지 */
  padding: 0.4rem 0 calc(80px + env(safe-area-inset-bottom, 0px));
  overflow-y: auto;
  flex: 1;
}

/* 260511 오후회의 #2: 카테고리 제목 볼드/색/크기 강화로 단락 구분 명확화 + 옆 라인 SVG 아이콘 */
.section-header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 1.4rem 1.4rem 0.7rem;
  font-size: 1.25rem;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.02em;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
}
.section-header:first-of-type {
  border-top: 0;
  background: transparent;
}
.section-icon {
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
  opacity: 0.85;
}

.section-list {
  list-style: none;
  padding: 0;
  margin: 0 0 0.3rem;
}

.hamburger-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.4rem;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f5;
}
.hamburger-item:active {
  background: #f8f8f8;
}
.hamburger-item.is-pending {
  opacity: 0.6;
}
.item-label {
  font-size: 1.3rem;
  font-weight: 500;
  color: #222;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
.pending-tag {
  font-size: 1rem;
  font-weight: 500;
  color: #b0b0b0;
  background: #f0f0f0;
  border-radius: 999px;
  padding: 0.15rem 0.5rem;
  font-style: normal;
}
.external-tag {
  font-size: 1rem;
  font-weight: 500;
  color: #2e6cf5;
  background: #e8f1ff;
  border-radius: 999px;
  padding: 0.15rem 0.5rem;
  font-style: normal;
}
</style>
